import { CURRENCY_COOKIE_NAME } from "@/config/constConf";
import { getCookieItem, jointImg } from "@/lib/commonService";
import { getEnv } from "@/src/utils/configUtils";

// 商品对象接口
interface AXONItem {
    item_variant_id?: string; // 商品变体ID(推荐)
    item_id: string | number; // 商品ID(必填)
    item_name: string; // 商品名称(必填)
    price?: number; // 商品价格(必填)
    quantity?: number; // 商品数量(必填,view_item事件可选)
    image_url?: string; // 商品图片URL(推荐)
    item_category_id?: number; // 商品分类ID(推荐)
    affiliation?: string; // 商品所属公司/商店
    discount?: number; // 商品折扣金额
    item_brand?: string; // 商品品牌
}

// 基础事件属性
interface BaseEventProperties {
    currency?: string; // ISO 4217货币代码(必填)
    items: AXONItem[]; // 商品列表(必填)
    value?: number; // 总金额
}

// 购买事件属性
interface PurchaseEventProperties extends BaseEventProperties {
    transaction_id: string; // 订单ID(必填)
    shipping?: number; // 运费(推荐)
    tax?: number; // 税费(推荐)
    user_id?: string; // 用户ID
}

interface AXONEvent {
    event_name: string;
    event_data?: Record<string, any>;
}

interface AddPaymentInfoEventProperties extends BaseEventProperties {
    payment_type?: string; // 支付方式(推荐)
}

/***
 * AppLovin
 * 【AppLovin媒体介绍和投放流程】：https://janzlz0n1f.feishu.cn/wiki/WwqSw7TRCi9UJ5kliTWcI0Lbnhc
 *
 * 安装完毕后可以使用AXON Pixel Helper Chrome 来调试和验证你的 Pixel 是否正确安装
 * AXON Pixel Helper 是一个 Google Chrome 扩展工具，专门用来帮助你调试和验证 Pixel 的安装。通过它，你可以看到你的网站触发的事件，并检查这些事件是否成功被记录。
 * 你可以在这里找到并安装 AXON Pixel Helper：https://chromewebstore.google.com/detail/axon-pixel-helper/cbnepobjhiakaeolafffknhlmmbhigbk
 */
class AXON {
    isInit = false;

    /**
     * 初始化AXON SDK
     * @param config AXON配置信息
     */
    init(host: string) {
        if (this.isInit) return;

        const AXON_EVENT_KEY = getEnv("AXON_EVENT_KEY", host);
        if (!AXON_EVENT_KEY) return;
        // 在Head标签中插入script标签执行初始化脚本
        const script = document.createElement("script");
        script.async = true;
        script.type = "text/javascript";
        script.innerHTML = `
        !function(e,r){var t=["https://s.axon.ai/pixel.js","https://c.albss.com/p/l/loader.iife.js"];if(!e.axon){var a=e.axon=function(){a.performOperation?a.performOperation.apply(a,arguments):a.operationQueue.push(arguments)};a.operationQueue=[],a.ts=Date.now(),
        a.eventKey='${AXON_EVENT_KEY}';for(var n=r.getElementsByTagName("script")[0],o=0;o<t.length;o++){var i=r.createElement("script");i.async=!0,i.src=t[o],n.parentNode.insertBefore(i,n)}}}(window,document);
    `;

        document.head.appendChild(script);
        const interval = setInterval(() => {
            if (window.axon) {
                this.isInit = true;
                clearInterval(interval);
                this.trackPageView();
            }
        }, 100);
    }

    getCurrency() {
        return getCookieItem(CURRENCY_COOKIE_NAME) || "USD";
    }

    /**
     * 追踪自定义事件
     * @param event 事件信息
     */
    track(event: AXONEvent) {
        if (!this.isInit) {
            console.warn("AXON not initialized");
            return;
        }
        const { event_name, event_data } = event;
        window.axon("track", event_name, event_data);
    }

    /**
     * 追踪页面访问事件
     */
    trackPageView() {
        this.track({
            event_name: "page_view",
        });
    }

    /**
     * 追踪用户登录事件
     * @param properties 事件属性
     * @param properties.user_id 用户ID
     */
    trackUserLogin(properties: { user_id: string }) {
        this.track({
            event_name: "login",
            event_data: properties,
        });
    }

    /**
     * 追踪用户注册事件
     * @param properties 事件属性
     * @param properties.method 注册方式
     * @param properties.user_id 用户ID
     */
    trackUserSignUp(properties: { method?: string; user_id: string }) {
        console.log("properties", properties);
        this.track({
            event_name: "sign_up",
            event_data: properties,
        });
    }

    /**
     * 追踪商品浏览事件
     */
    trackViewItem(properties: BaseEventProperties) {
        const { currency, ...rest } = properties;
        this.track({
            event_name: "view_item",
            event_data: {
                currency: currency || this.getCurrency(),
                ...rest,
            },
        });
    }

    /**
     * 追踪加入购物车事件
     */
    trackAddToCart(properties: BaseEventProperties) {
        const { currency, ...rest } = properties;
        this.track({
            event_name: "add_to_cart",
            event_data: {
                currency: currency || this.getCurrency(),
                ...rest,
            },
        });
    }

    /**
     * 追踪开始结账事件
     */
    trackBeginCheckout(properties: BaseEventProperties) {
        const { currency, ...rest } = properties;
        this.track({
            event_name: "begin_checkout",
            event_data: {
                currency: currency || this.getCurrency(),
                ...rest,
            },
        });
    }

    /**
     * 追踪添加支付信息事件
     */
    trackAddPaymentInfo(properties: AddPaymentInfoEventProperties) {
        const { currency, ...rest } = properties;
        this.track({
            event_name: "add_payment_info",
            event_data: {
                currency: currency || this.getCurrency(),
                ...rest,
            },
        });
    }

    /**
     * 追踪购买事件
     */
    trackPurchase(properties: PurchaseEventProperties) {
        const { currency, ...rest } = properties;
        this.track({
            event_name: "purchase",
            event_data: {
                currency: currency || this.getCurrency(),
                ...rest,
            },
        });
    }
}
// 导出单例实例
const axon = new AXON();
export default axon;

// 获取事件商品参数
export function getEventProductItemParams(productList: any[]) {
    return productList.map((item: any) => {
        let {
            products_id,
            products_model,
            products_name,
            quantity,
            color_id,
            total_price,
            color_image,
        } = item;

        return {
            item_id: products_id,
            item_name: products_model || products_name || "",
            quantity: quantity || 1,
            item_variant_id: color_id,
            price: total_price,
            item_category_id: 524,
            image_url: jointImg({
                productId: products_id,
                size: "middle",
                imgTotal: 2,
                name: color_image,
            }),
        };
    });
}
